import { Broker } from "./brokers"
import { OrderType } from "./common"

export enum ExecutionType {
  HERE = "here",
  VOL_INLINE = "vol_inline",
  VWAP = "vwap",
  TWAP = "twap",
  PAIR = "pair",
  PRE_MARKET = "pre_market",
  FUTURE = "future",
  STRICT_LIMIT = "strict_limit",
  SOR = "sor",
  SONAR_LOW = "sonar_low",
  SONAR_MEDIUM = "sonar_medium",
  SONAR_HIGH = "sonar_high",
  SMA_PREMKT = "sma_premkt",
  CAPUTURE_LOW = "capture_low",
  CAPUTURE_MEDIUM = "capture_medium",
  CAPUTURE_HIGH = "capture_high",
  GET_DONE_HERE_LOW = "get_done_here_low",
  GET_DONE_HERE_MEDIUM = "get_done_here_medium",
  GET_DONE_HERE_HIGH = "get_done_here_high",
  AUCTION_ONLY = "auction_only",
  AQUA_LOW = "aqua_low",
  AQUA_MEDIUM = "aqua_medium",
  AQUA_HIGH = "aqua_high",
}

/**
 * Nested map of broker and order types to their supported execution types
 */
export const BROKER_ORDER_EXECUTION_TYPES: Record<Broker, Record<OrderType, ExecutionType[]>> = {
  [Broker.GS]: {
    [OrderType.MARKET]: [
      ExecutionType.SONAR_MEDIUM,
      ExecutionType.SONAR_LOW,
      ExecutionType.SONAR_HIGH,
      ExecutionType.SOR,
      ExecutionType.VWAP,
      ExecutionType.TWAP,
      ExecutionType.AUCTION_ONLY,
    ],
    [OrderType.LIMIT]: [
      ExecutionType.SONAR_MEDIUM,
      ExecutionType.SONAR_LOW,
      ExecutionType.SONAR_HIGH,
      ExecutionType.SOR,
      ExecutionType.VWAP,
      ExecutionType.TWAP,
      ExecutionType.AUCTION_ONLY,
    ],
  },
  [Broker.JPM]: {
    [OrderType.MARKET]: [
      ExecutionType.AQUA_MEDIUM,
      ExecutionType.AQUA_LOW,
      ExecutionType.AQUA_HIGH,
      ExecutionType.SOR,
      ExecutionType.TWAP,
      ExecutionType.VWAP,
    ],
    [OrderType.LIMIT]: [
      ExecutionType.AQUA_MEDIUM,
      ExecutionType.AQUA_LOW,
      ExecutionType.AQUA_HIGH,
      ExecutionType.SOR,
      ExecutionType.TWAP,
      ExecutionType.VWAP,
    ],
  },
  [Broker.TOUR]: {
    [OrderType.MARKET]: [],
    [OrderType.LIMIT]: [],
  },
  [Broker.BOFA]: {
    [OrderType.MARKET]: [
      ExecutionType.GET_DONE_HERE_MEDIUM,
      ExecutionType.GET_DONE_HERE_LOW,
      ExecutionType.GET_DONE_HERE_HIGH,
      ExecutionType.SMA_PREMKT,
      ExecutionType.CAPUTURE_LOW,
      ExecutionType.CAPUTURE_MEDIUM,
      ExecutionType.CAPUTURE_HIGH,
      ExecutionType.VWAP,
      ExecutionType.TWAP,
    ],
    [OrderType.LIMIT]: [
      ExecutionType.GET_DONE_HERE_MEDIUM,
      ExecutionType.GET_DONE_HERE_LOW,
      ExecutionType.GET_DONE_HERE_HIGH,
      ExecutionType.SMA_PREMKT,
      ExecutionType.CAPUTURE_LOW,
      ExecutionType.CAPUTURE_MEDIUM,
      ExecutionType.CAPUTURE_HIGH,
      ExecutionType.VWAP,
      ExecutionType.TWAP,
    ],
  },
  [Broker.EXEC_DESK]: {
    [OrderType.MARKET]: [
      ExecutionType.HERE,
      ExecutionType.VOL_INLINE,
      ExecutionType.VWAP,
      ExecutionType.TWAP,
      ExecutionType.PAIR,
      ExecutionType.PRE_MARKET,
      ExecutionType.FUTURE,
    ],
    [OrderType.LIMIT]: [
      ExecutionType.HERE,
      ExecutionType.VOL_INLINE,
      ExecutionType.VWAP,
      ExecutionType.TWAP,
      ExecutionType.PAIR,
      ExecutionType.PRE_MARKET,
      ExecutionType.FUTURE,
      ExecutionType.STRICT_LIMIT,
    ],
  },
  [Broker.EXEC_DESK_PM]: {
    [OrderType.MARKET]: [],
    [OrderType.LIMIT]: [],
  },
}

/**
 * Get the execution types for a broker and order type
 */
export const getExecutionTypesForBrokerAndOrderType = (
  broker: Broker | undefined,
  orderType: OrderType | undefined,
): Record<string, string> => {
  let executionTypes: ExecutionType[]

  if (!broker || !orderType) {
    executionTypes = BROKER_ORDER_EXECUTION_TYPES[Broker.EXEC_DESK][OrderType.MARKET]
  } else {
    const brokerTypes =
      BROKER_ORDER_EXECUTION_TYPES[broker] || BROKER_ORDER_EXECUTION_TYPES[Broker.EXEC_DESK]
    executionTypes = brokerTypes[orderType] || brokerTypes[OrderType.MARKET]
  }

  // Convert array to Record<string, string>
  return executionTypes.reduce(
    (acc, type) => {
      acc[type] = type // Using the enum value as both key and display value
      return acc
    },
    {} as Record<string, string>,
  )
}

export const getBrokerFromExecutionType = (executionType: ExecutionType): Broker => {
  return Object.keys(BROKER_ORDER_EXECUTION_TYPES).find(broker =>
    BROKER_ORDER_EXECUTION_TYPES[broker as Broker][OrderType.MARKET].includes(executionType),
  ) as Broker
}
